import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { calcMaxBonusSpend } from '../utils/bonus';
import LoadErrorScreen from './LoadErrorScreen';

const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [rentalDays, setRentalDays] = useState(1);
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [cartTab, setCartTab] = useState(0); // 0 = Аренда, 1 = Магазин
  const [productOrderDialogOpen, setProductOrderDialogOpen] = useState(false);
  const [selectedProductBranch, setSelectedProductBranch] = useState('');
  const [productBranches, setProductBranches] = useState([]);
  const [creatingProductOrder, setCreatingProductOrder] = useState(false);
  const [bonusBalance, setBonusBalance] = useState(0);
  const [useBonus, setUseBonus] = useState(false);

  const fetchBonusBalance = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/bonus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.ok) setBonusBalance(Number(data.balance) || 0);
    } catch (e) {
      console.error('Ошибка загрузки бонусов:', e);
    }
  };

  useEffect(() => {
    fetchCartItems();
    fetchBranches();
    fetchBonusBalance();
    
    // Загружаем параметры из URL или localStorage для предзаполнения формы
    const searchParams = new URLSearchParams(location.search);
    const urlDateFrom = searchParams.get('dateFrom');
    const urlDateTo = searchParams.get('dateTo');
    const urlBranchId = searchParams.get('branchId');
    
    // Параметры из localStorage
    const savedDateFrom = localStorage.getItem('dateFrom');
    const savedDateTo = localStorage.getItem('dateTo');
    const savedBranch = localStorage.getItem('selectedBranch');
    
    // Используем параметры из URL (приоритет), иначе из localStorage, иначе сегодня
    const today = new Date().toISOString().split('T')[0];
    const dateFrom = urlDateFrom || savedDateFrom || today;
    const dateTo = urlDateTo || savedDateTo || today;
    const branchId = urlBranchId || savedBranch || '';
    
    setRentalStartDate(dateFrom);
    setRentalEndDate(dateTo);
    
    // branchId будет установлен после загрузки branches в fetchBranches
    // Временное сохранение для использования после загрузки branches
    if (branchId) {
      // Сохраняем в state, но преобразование в _id произойдет в fetchBranches
      setSelectedBranch(branchId);
    }
  }, []);

  // Устанавливаем selectedBranch (_id) после загрузки branches, если он еще не установлен
  useEffect(() => {
    if (branches.length > 0 && selectedBranch) {
      // Проверяем, является ли selectedBranch уже _id (24 символа hex) или branchId (строка типа "b1")
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(selectedBranch);
      
      if (!isObjectId) {
        // Если это branchId (строка), находим соответствующий филиал и берем его _id
        const branch = branches.find(b => b.branchId === selectedBranch || b._id === selectedBranch);
        if (branch) {
          setSelectedBranch(branch._id);
        }
      }
    }
  }, [branches, selectedBranch]);

  // Автоматический расчет количества дней на основе дат начала и окончания
  useEffect(() => {
    if (rentalStartDate && rentalEndDate) {
      const startDate = new Date(rentalStartDate);
      const endDate = new Date(rentalEndDate);
      
      // Если даты совпадают - это 1 день
      if (rentalStartDate === rentalEndDate) {
        setRentalDays(1);
      } else if (endDate >= startDate) {
        // Рассчитываем количество дней включительно (разница + 1)
        const diffTime = endDate - startDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setRentalDays(Math.max(1, diffDays));
      } else {
        // Если дата окончания раньше даты начала, устанавливаем 1 день
        setRentalDays(1);
        setRentalEndDate(rentalStartDate);
      }
    }
  }, [rentalStartDate, rentalEndDate]);

  const fetchCartItems = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
      } else if (response.status === 401) {
        navigate('/');
      }
    } catch (error) {
      console.error('Ошибка загрузки корзины:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`);
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        
        // Если selectedBranch еще не установлен, пытаемся установить его
        if (!selectedBranch) {
          // Сначала проверяем localStorage и URL
          const searchParams = new URLSearchParams(location.search);
          const urlBranchId = searchParams.get('branchId');
          const savedBranch = localStorage.getItem('selectedBranch');
          const branchIdToUse = urlBranchId || savedBranch;
          
          if (branchIdToUse) {
            // Ищем филиал по branchId (строка типа "b1") или _id
            const branch = data.find(b => b.branchId === branchIdToUse || b._id === branchIdToUse);
            if (branch) {
              // Используем _id филиала для selectedBranch
              setSelectedBranch(branch._id);
            }
          } else if (data.length > 0 && cartItems.length > 0) {
            // Если нет сохраненного branchId, используем branchId первого товара в корзине
            const firstItemBranchId = cartItems[0]?.itemId?.branchId;
            const branch = data.find(b => b.branchId === firstItemBranchId);
            if (branch) {
              setSelectedBranch(branch._id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки филиалов:', error);
    }
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (response.ok) {
        await fetchCartItems();
      }
    } catch (error) {
      console.error('Ошибка обновления количества:', error);
    }
  };

  const handleRemoveItem = async (itemId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchCartItems();
      }
    } catch (error) {
      console.error('Ошибка удаления товара:', error);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      const trimmed = photo.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      if (trimmed.startsWith('/')) {
        return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
      }
      return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
    }
    return null;
  };

  const rentalItems = cartItems.filter((i) => i.itemId);
  const productItems = cartItems.filter((i) => i.productId);

  // Переключаем на вкладку с контентом, если текущая пуста
  useEffect(() => {
    if (cartTab === 0 && rentalItems.length === 0 && productItems.length > 0) setCartTab(1);
    else if (cartTab === 1 && productItems.length === 0 && rentalItems.length > 0) setCartTab(0);
  }, [cartTab, rentalItems.length, productItems.length]);

  const getTotalPrice = () => {
    return rentalItems.reduce((sum, item) => {
      const pricePerDay = item.itemId?.price || 0;
      const quantity = item.quantity || 1;
      return sum + (pricePerDay * quantity * rentalDays);
    }, 0);
  };

  const getTotalDeposit = () => {
    return rentalItems.reduce((sum, item) => {
      let depositPerUnit = 700;
      if (item.itemId?.specificFields?.depositPerUnit !== undefined && item.itemId?.specificFields?.depositPerUnit !== null) {
        depositPerUnit = item.itemId.specificFields.depositPerUnit;
      } else if (item.itemId?.depositPerUnit !== undefined && item.itemId?.depositPerUnit !== null) {
        depositPerUnit = item.itemId.depositPerUnit;
      }
      const quantity = item.quantity || 1;
      return sum + (depositPerUnit * quantity);
    }, 0);
  };

  const getTotalProductsPrice = () => {
    return productItems.reduce((sum, item) => {
      const price = item.productId?.price ?? 0;
      const quantity = item.quantity || 1;
      return sum + (price * quantity);
    }, 0);
  };

  const rentTotal = getTotalPrice();
  const depositTotal = getTotalDeposit();
  const maxBonusApplicable = calcMaxBonusSpend(rentTotal, bonusBalance);
  const bonusToApply = useBonus ? maxBonusApplicable : 0;
  const cashDue = Math.max(0, rentTotal - bonusToApply) + depositTotal;

  const handleCreateOrder = async () => {
    if (!selectedBranch) {
      setError('Выберите филиал для выдачи');
      return;
    }

    if (!rentalStartDate || !rentalEndDate) {
      setError('Укажите период аренды');
      return;
    }

    setCreatingOrder(true);
    setError('');

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      // Создаем заказ только из позиций аренды
      const orderItems = rentalItems.map(item => ({
        itemId: item.itemId._id,
        quantity: item.quantity || 1,
      }));
      if (orderItems.length === 0) {
        setError('Нет позиций аренды для оформления заказа.');
        setCreatingOrder(false);
        return;
      }

      // Определяем период аренды
      const startDate = new Date(rentalStartDate);
      const endDate = new Date(rentalEndDate);

      // Если аренда на один день
      let from, to, unit, periodAmount;
      if (rentalDays === 1) {
        // Один день: 10:00 - 21:00, но цена за полный день
        from = new Date(startDate);
        from.setHours(10, 0, 0, 0);
        to = new Date(startDate);
        to.setHours(21, 0, 0, 0);
        unit = 'days';
        periodAmount = 1;
      } else {
        // Несколько дней: полные дни от начала дня начала до конца дня окончания
        from = new Date(startDate);
        from.setHours(0, 0, 0, 0);
        to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        unit = 'days';
        // Считаем количество дней включая оба дня
        periodAmount = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      }

      // Проверяем, что все позиции аренды из одного филиала
      const itemBranchIds = new Set();
      rentalItems.forEach(item => {
        if (item.itemId?.branchId) {
          itemBranchIds.add(item.itemId.branchId);
        }
      });

      if (orderItems.length > 0 && itemBranchIds.size > 1) {
        setError('Все позиции аренды должны быть из одного филиала');
        setCreatingOrder(false);
        return;
      }

      // Филиал определяется по привязке оборудования (один на все позиции). Разрешаем _id для API.
      let branchIdToSend = selectedBranch;
      const selectedBranchObj = branches.find(b => b._id === selectedBranch || b.branchId === selectedBranch);
      if (selectedBranchObj) {
        branchIdToSend = selectedBranchObj._id;
      }

      const orderData = {
        items: orderItems,
        branchId: branchIdToSend,
        from: from.toISOString(),
        to: to.toISOString(),
        unit,
        periodAmount,
        useBonus: useBonus && bonusToApply > 0,
      };

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Ошибка создания заказа' }));
        throw new Error(errorData.message || 'Ошибка создания заказа');
      }

      const orderData_result = await response.json();

      navigate('/orders');

      fetchBonusBalance().catch(() => {});

      Promise.all(
        rentalItems.map((item) => {
          const id = item.itemId?._id || item.itemId;
          if (!id) return Promise.resolve();
          return fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
        })
      ).then(() => fetchCartItems()).catch(() => {});
    } catch (error) {
      console.error('Ошибка создания заказа:', error);
      setError(error.message || 'Ошибка создания заказа');
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleStartDateChange = (date) => {
    setRentalStartDate(date);
    if (rentalEndDate && date && rentalEndDate < date) {
      setRentalEndDate(date);
    }
  };

  const handleOpenProductOrderDialog = async () => {
    setProductOrderDialogOpen(true);
    setSelectedProductBranch('');
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/product-orders/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setProductBranches(list);
        if (list.length === 1) setSelectedProductBranch(list[0]._id || list[0].branchId || '');
      }
    } catch (e) {
      console.error('Ошибка загрузки центров выдачи:', e);
    }
  };

  const handleCreateProductOrder = async () => {
    if (!selectedProductBranch) {
      setError('Выберите центр выдачи');
      return;
    }
    setCreatingProductOrder(true);
    setError('');
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/product-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ branchId: selectedProductBranch }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Ошибка оформления заказа');
      setProductOrderDialogOpen(false);
      await fetchCartItems();
      setError('');
      if (window.confirm('Заказ оформлен. Оплата при получении. Перейти к заказам?')) {
        navigate('/orders');
      }
    } catch (err) {
      setError(err.message || 'Ошибка оформления заказа');
    } finally {
      setCreatingProductOrder(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Загрузка корзины...</Typography>
      </Container>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (cartItems.length === 0) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <ShoppingCartIcon sx={{ fontSize: 80, color: '#8B7D6B', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Корзина пуста
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2, backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
        >
          Перейти к каталогу
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#000000', fontWeight: 'bold' }}>
        Корзина
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs value={cartTab} onChange={(_, v) => setCartTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={rentalItems.length > 0 ? `Аренда (${rentalItems.reduce((s, i) => s + (i.quantity || 1), 0)})` : 'Аренда'} id="cart-tab-0" />
        <Tab label={productItems.length > 0 ? `Магазин (${productItems.reduce((s, i) => s + (i.quantity || 1), 0)})` : 'Магазин'} id="cart-tab-1" />
      </Tabs>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {cartTab === 0 && (
            <>
              {rentalItems.length > 0 && (
                <Card sx={{ mb: 2, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Период аренды
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Дата начала"
                        value={rentalStartDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Дата окончания"
                        value={rentalEndDate}
                        onChange={(e) => setRentalEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: rentalStartDate || new Date().toISOString().split('T')[0] }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {rentalDays} {rentalDays === 1 ? 'день' : rentalDays < 5 ? 'дня' : 'дней'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              )}
              {rentalItems.length > 0 ? (
                rentalItems.map((cartItem) => {
                  const item = cartItem.itemId;
                  const photoUrl = item?.photos?.[0] ? getPhotoUrl(item.photos[0]) : null;
                  const id = item?._id || item;
                  return (
                    <Card key={`item-${id}`} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/item/${item?.inventoryNumber || item?._id}`)}>
                      <Grid container>
                        <Grid item xs={12} sm={4}>
                          <CardMedia component="img" height="200" image={photoUrl || 'https://via.placeholder.com/300'} alt={item?.title} sx={{ objectFit: 'cover' }} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <CardContent>
                            <Chip label="Аренда" size="small" sx={{ mb: 1, backgroundColor: '#F9E79F' }} />
                            <Typography variant="h6" gutterBottom>{item?.title}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>{item?.inventoryNumber}</Typography>
                            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{item?.price} ₽/сутки</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(id, (cartItem.quantity || 1) - 1); }}><RemoveIcon /></IconButton>
                              <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>{cartItem.quantity || 1}</Typography>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(id, (cartItem.quantity || 1) + 1); }}><AddIcon /></IconButton>
                              <Box sx={{ flexGrow: 1 }} />
                              <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleRemoveItem(id); }}><DeleteIcon /></IconButton>
                            </Box>
                          </CardContent>
                        </Grid>
                      </Grid>
                    </Card>
                  );
                })
              ) : (
                <Typography color="text.secondary">В этой вкладке пока ничего нет</Typography>
              )}
            </>
          )}

          {cartTab === 1 && (
            productItems.length > 0 ? (
              productItems.map((cartItem) => {
                const product = cartItem.productId;
                const photoUrl = product?.photos?.[0] ? getPhotoUrl(product.photos[0]) : null;
                const id = product._id || product;
                return (
                  <Card key={`product-${id}`} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => navigate(`/products/${product.productNumber}`)}>
                    <Grid container>
                      <Grid item xs={12} sm={4}>
                        <CardMedia component="img" height="200" image={photoUrl || 'https://via.placeholder.com/300'} alt={product?.title} sx={{ objectFit: 'cover' }} />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <CardContent>
                          <Chip label="Продажа" size="small" sx={{ mb: 1, backgroundColor: '#2e7d32', color: '#fff' }} />
                          <Typography variant="h6" gutterBottom>{product?.title}</Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>{product?.productNumber}</Typography>
                          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{(product?.price ?? 0) * (cartItem.quantity || 1)} ₽</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(id, (cartItem.quantity || 1) - 1); }}><RemoveIcon /></IconButton>
                            <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>{cartItem.quantity || 1}</Typography>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateQuantity(id, (cartItem.quantity || 1) + 1); }}><AddIcon /></IconButton>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleRemoveItem(id); }}><DeleteIcon /></IconButton>
                          </Box>
                        </CardContent>
                      </Grid>
                    </Grid>
                  </Card>
                );
              })
            ) : (
              <Typography color="text.secondary">В этой вкладке пока ничего нет</Typography>
            )
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              {cartTab === 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>Итого (аренда)</Typography>
                  <Divider sx={{ my: 2 }} />
                  {rentalItems.length > 0 ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Аренда ({rentalDays} {rentalDays === 1 ? 'день' : 'дней'}): {rentTotal.toLocaleString('ru-RU')} ₽
                        </Typography>
                        <Typography variant="body2" gutterBottom>Залог: {depositTotal.toLocaleString('ru-RU')} ₽</Typography>
                        {bonusBalance > 0 && (
                          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, backgroundColor: 'grey.50' }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              На бонусном счёте: {bonusBalance.toLocaleString('ru-RU')} ₽
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={useBonus}
                                  onChange={(e) => setUseBonus(e.target.checked)}
                                  disabled={maxBonusApplicable <= 0}
                                  color="primary"
                                />
                              }
                              label={
                                maxBonusApplicable > 0
                                  ? `Списать до ${maxBonusApplicable.toLocaleString('ru-RU')} ₽ бонусами (до 30% аренды)`
                                  : 'Недостаточно бонусов для списания'
                              }
                            />
                          </Box>
                        )}
                        {bonusToApply > 0 && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                            Списано бонусами: −{bonusToApply.toLocaleString('ru-RU')} ₽
                          </Typography>
                        )}
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>К оплате: {cashDue.toLocaleString('ru-RU')} ₽</Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          if (rentalItems.length > 0) {
                            const bid = rentalItems[0].itemId?.branchId;
                            if (bid) {
                              const b = branches.find((x) => x.branchId === bid || x._id === bid);
                              setSelectedBranch(b ? b._id : bid);
                            }
                          }
                          setOrderDialogOpen(true);
                        }}
                        sx={{ mt: 2, backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
                      >
                        Оформить заказ
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Добавьте активы для аренды</Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>Итого (магазин)</Typography>
                  <Divider sx={{ my: 2 }} />
                  {productItems.length > 0 ? (
                    <>
                      <Typography variant="body2" gutterBottom>Товаров: {productItems.reduce((s, i) => s + (i.quantity || 1), 0)}</Typography>
                      <Typography variant="h6" sx={{ mt: 2 }}>Сумма: {getTotalProductsPrice().toLocaleString('ru-RU')} ₽</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Оплата при получении</Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleOpenProductOrderDialog}
                        sx={{ mt: 2, backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
                      >
                        Оформить заказ
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Добавьте товары из каталога</Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Диалог оформления заказа (аренда): филиал определяется по привязке оборудования, выбор не показываем */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Оформление заказа</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Количество дней"
              value={rentalDays}
              inputProps={{ min: 1, readOnly: true }}
              disabled
              helperText="Рассчитывается автоматически на основе выбранного периода"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              type="date"
              label="Дата начала"
              value={rentalStartDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              type="date"
              label="Дата окончания"
              value={rentalEndDate}
              onChange={(e) => setRentalEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: rentalStartDate || new Date().toISOString().split('T')[0] }}
              sx={{ mb: 2 }}
            />

            {bonusBalance > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useBonus}
                      onChange={(e) => setUseBonus(e.target.checked)}
                      disabled={maxBonusApplicable <= 0}
                    />
                  }
                  label={
                    useBonus && bonusToApply > 0
                      ? `Использовать ${bonusToApply.toLocaleString('ru-RU')} ₽ бонусами`
                      : `Использовать бонусы (до ${maxBonusApplicable.toLocaleString('ru-RU')} ₽)`
                  }
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={handleCreateOrder}
            variant="contained"
            disabled={creatingOrder || !selectedBranch || !rentalStartDate || !rentalEndDate}
            sx={{ backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
          >
            {creatingOrder ? 'Создание...' : 'Создать заказ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог оформления заказа товаров (магазин) */}
      <Dialog open={productOrderDialogOpen} onClose={() => setProductOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Оформление заказа (товары)</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Выберите центр выдачи. Оплата при получении.
            </Typography>
            <TextField
              select
              fullWidth
              label="Центр выдачи"
              value={selectedProductBranch}
              onChange={(e) => setSelectedProductBranch(e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            >
              <option value="">Выберите центр выдачи</option>
              {productBranches.map((b) => (
                <option key={b._id} value={b._id}>
                  {[b.city, b.address].filter(Boolean).join(' — ')}
                  {b.deliveryLabel ? ` (${b.deliveryLabel})` : ''}
                </option>
              ))}
            </TextField>
            {productBranches.length === 0 && productOrderDialogOpen && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Нет общего центра выдачи для всех товаров в корзине. Убедитесь, что все товары доставляются в один центр.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductOrderDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleCreateProductOrder}
            disabled={creatingProductOrder || !selectedProductBranch}
            sx={{ backgroundColor: '#8B7D6B', '&:hover': { backgroundColor: '#6B5D4B' } }}
          >
            {creatingProductOrder ? 'Оформление...' : 'Оформить заказ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CartPage;

